import styled, { css, keyframes } from 'styled-components';

import { getThemeColor, getTypographyStyle, media } from '@utils/styled';

import { SHOP_EVENT_AVAILABILITY_LABEL } from '@common/constants/shopEvent';
import Typography from '@common/components/Typography';

export const ProductWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${getThemeColor('bluegrey.95')};

  a {
    color: inherit;

    &:hover {
      text-decoration: none;
    }
  }
`;

export const ProductLink = styled.a`
  cursor: pointer;

  ${({ isSoldOut }) =>
    isSoldOut &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        opacity: 0.8;
        display: block;
        background-color: ${getThemeColor('bluegrey.95')};
      }
    `}
`;

export const ProductImageWrapper = styled.div`
  position: relative;
`;

export const ProductImageDiscount = styled(Typography).attrs({
  customAs: 'span',
  variant: 'body4',
})`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.getFluidSpacing('right', 'scale', 6)}
  ${({ theme }) => theme.getFluidSpacing('bottom', 'equal', 4)}
  height: 2rem;
  ${({ theme }) => theme.getFluidSpacing('padding-right', 'scale', 18)};
  ${({ theme }) => theme.getFluidSpacing('padding-left', 'scale', 18)};
  background-color: ${getThemeColor('hue.green')};
  color: ${getThemeColor('grey.100')};
  font-weight: 700;

  ${({ theme }) =>
    theme.layoutPreference === 'grid' &&
    css`
      padding-right: 0.5rem;
      padding-left: 0.5rem;
    `}

  ${({ theme }) =>
    theme.layoutPreference === 'column' &&
    css`
      ${media.from480down`
        ${getTypographyStyle('paragraph1')};
        ${theme.getFluidSpacing('height', 'scale', 2)};
        margin-bottom: 0.4rem;
        padding: 0 1.5rem;
        font-weight: 700;
      `}
    `};

  ${media.from480up`
    ${getTypographyStyle('h5')};
    ${({ theme }) => theme.getFluidSpacing('height', 'scale', 2)};
  `}
`;

const shine = keyframes`
  from {
    background-position-x: 100%;
  }

  to {
    background-position-x: 0%;
  }
`;

export const ProductAvailabilityLabel = styled.span`
  ${getTypographyStyle('body4')};
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  margin: 0;
  ${({ $type }) =>
    $type === SHOP_EVENT_AVAILABILITY_LABEL.SOLDOUT &&
    css`
      background-color: ${getThemeColor('hue.red')};
    `}
  color: ${getThemeColor('grey.100')};
  ${({ theme }) => theme.getFluidSpacing('padding-top', 'scale', 8)};
  ${({ theme }) => theme.getFluidSpacing('padding-right', 'widthBased', 5)};
  ${({ theme }) => theme.getFluidSpacing('padding-bottom', 'scale', 8)};
  ${({ theme }) => theme.getFluidSpacing('padding-left', 'widthBased', 5)};

  ${({ $type, shouldAnimate }) =>
    $type === SHOP_EVENT_AVAILABILITY_LABEL.LOW &&
    css`
      background: ${getThemeColor('hue.orange')};
      background: linear-gradient(
        90deg,
        ${getThemeColor('hue.orange')} 20%,
        ${getThemeColor('hue.darkOrange')} 40%,
        ${getThemeColor('hue.darkOrange')} 60%,
        ${getThemeColor('hue.orange')} 80%
      );
      background-size: 500% 100%;
      ${shouldAnimate &&
      css`
        animation: ${shine} 1.5s linear 1;
      `}
    `}

  ${media.from480down`
    ${({ theme }) =>
      theme.layoutPreference === 'column' &&
      css`
        ${theme.getFluidSpacing('padding-top', 'equal', 5)};
        ${theme.getFluidSpacing('padding-right', 'widthBased', 0)};
        ${theme.getFluidSpacing('padding-bottom', 'equal', 5)};
        ${theme.getFluidSpacing('padding-left', 'widthBased', 0)};
      `}
  `}

  ${media.from480up`
    ${getTypographyStyle('body3')};
  `}
`;

export const ProductContent = styled.div`
  flex-grow: 1;
  ${({ theme }) => theme.getFluidSpacing('padding-top', 'scale', 6)};
  ${({ theme }) => theme.getFluidSpacing('padding-right', 'widthBased', 5)};
  ${({ theme }) => theme.getFluidSpacing('padding-bottom', 'scale', 5)};
  ${({ theme }) => theme.getFluidSpacing('padding-left', 'widthBased', 5)};

  ${media.from480down`
    ${({ theme }) =>
      theme.layoutPreference === 'column' &&
      css`
        ${theme.getFluidSpacing('padding-left', 'widthBased', 0)};
        ${theme.getFluidSpacing('padding-right', 'widthBased', 0)};
      `}
  `}
`;

export const Price = styled(Typography).attrs(({ theme }) => ({
  customAs: 'p',
  variant: theme.layoutPreference === 'column' ? 'h4' : 'paragraph2',
}))`
  margin: 0;
  font-weight: 700;
  ${({ isSale }) =>
    isSale &&
    css`
      color: ${getThemeColor('hue.green')};
    `}

  ${media.from480up`
    ${getTypographyStyle('h4')};
  `}
`;

export const Title = styled(Typography).attrs(({ theme }) => ({
  customAs: 'h3',
  variant: theme.layoutPreference === 'column' ? 'h5' : 'paragraph2',
}))`
  ${({ theme }) => theme.getFluidSpacing('margin-bottom', 'equal', 6)};
  font-weight: 700;

  ${media.from480up`
    ${getTypographyStyle('h5')};
  `}
`;
